/**
 * Audit Log Service - API calls for Audit Logs
 * 
 * Handles all audit log-related API operations:
 * - List audit logs with filtering and pagination
 */

import { PROMOS_ENDPOINTS } from './endpoints';
import secureApi from '../../utils/secureApi';
import {
  AuditLog,
  AuditLogListResponse,
  AuditLogListParams,
} from './types/auditLogTypes';

/**
 * Transform API response to AuditLog interface
 */
const transformAuditLogResponse = (item: any): AuditLog => {
  // Handle changedBy - it can be a string or an object with {_id, email, name}
  let changedBy: string | null = null;
  if (item.changedBy) {
    if (typeof item.changedBy === 'string') {
      changedBy = item.changedBy;
    } else if (typeof item.changedBy === 'object') {
      // Extract name or email from the object
      changedBy = item.changedBy.name || item.changedBy.email || 'System';
    }
  }

  return {
    _id: item._id,
    action: item.action,
    changedBy: changedBy,
    changes: item.changes,
    promoName: item.promoName,
    timestamp: item.timestamp,
  };
};

/**
 * Get audit logs with filtering and pagination
 */
export const getAuditLogs = async (params: AuditLogListParams = {}): Promise<AuditLogListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add query parameters
    if (params.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.promoId) queryParams.append('promoId', params.promoId);
    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.action) queryParams.append('action', params.action);
    if (params.promoName) queryParams.append('promoName', params.promoName);
    if (params.startAt) queryParams.append('startAt', params.startAt);
    if (params.endAt) queryParams.append('endAt', params.endAt);
    
    const endpoint = `${PROMOS_ENDPOINTS.AUDIT_LOGS.LIST}?${queryParams.toString()}`;
    const response = await secureApi.get(endpoint);
    
    // Transform API response
    const data = response.data?.data || response.data;
    
    if (data.results && Array.isArray(data.results)) {
      const results = data.results.map(transformAuditLogResponse);
      const totalCount = data.totalCount || 0;
      
      return {
        results,
        totalCount,
      };
    } else if (Array.isArray(data)) {
      // Handle array response
      const results = data.map(transformAuditLogResponse);
      return {
        results,
        totalCount: results.length,
      };
    } else {
      // Handle unexpected format
      return {
        results: [],
        totalCount: 0,
      };
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to fetch audit logs. Please try again.';
    throw new Error(errorMessage);
  }
};
